<template>
  <v-card class="address-form">
    <v-card-title class="d-flex justify-space-between align-center">
      <h3>{{ isEditing ? 'Edit Address' : 'Add New Address' }}</h3>
      <v-btn
        icon="mdi-close"
        variant="text"
        @click="handleClose"
      />
    </v-card-title>

    <v-card-text>
      <v-form ref="form" v-model="formValid" @submit.prevent="handleSubmit">
        <v-row>
          <v-col cols="12">
            <v-autocomplete
              v-model="selectedPlace"
              :items="places"
              item-title="full_name"
              item-value="place_id"
              return-object
              label="Search for address *"
              placeholder="Start typing to search for an address..."
              :loading="searchLoading"
              :rules="addressRules"
              variant="outlined"
              density="comfortable"
              hide-no-data
              clearable
              @update:search="handleSearch"
            >
              <template v-slot:prepend-inner>
                <v-icon icon="mdi-map-marker" />
              </template>
              <template v-slot:item="{ props: itemProps, item }">
                <v-list-item
                  v-bind="itemProps"
                  :title="item.raw.full_name"
                >
                  <template v-slot:prepend>
                    <v-icon icon="mdi-map-marker" />
                  </template>
                </v-list-item>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.address_line_1"
              label="Address Line 1"
              placeholder="Street number, apartment, suite, unit, building"
              variant="outlined"
              density="comfortable"
            />
          </v-col>

          <v-col cols="12">
            <v-text-field
              v-model="formData.address_nickname"
              label="Address Nickname (Optional)"
              placeholder="e.g., Home, Work, Mom's House"
              variant="outlined"
              density="comfortable"
            />
          </v-col>

          <v-col cols="12">
            <v-checkbox
              v-model="formData.is_default"
              label="Set as default address"
              density="comfortable"
              color="primary"
            />
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>

    <v-card-actions class="pa-4">
      <v-spacer />
      <v-btn
        variant="text"
        @click="handleClose"
        :disabled="loading"
      >
        Cancel
      </v-btn>
      <v-btn
        color="primary"
        @click="handleSubmit"
        :loading="loading"
        :disabled="!formValid || !selectedPlace"
      >
        {{ isEditing ? 'Update Address' : 'Add Address' }}
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import addressService from '@/services/AddressService';
import placesService from '@/services/PlacesService';
import { debounce } from 'lodash';
import useSnackbar from '@/composables/useSnackbar';

export default {
  name: 'SimpleAddressForm',
  props: {
    address: {
      type: Object,
      default: null,
    },
    isEditing: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      formValid: false,
      loading: false,
      searchLoading: false,
      selectedPlace: null,
      places: [],
      formData: {
        place_id: '',
        full_name: '',
        address_line_1: '',
        types: [],
        address_nickname: '',
        is_default: false,
      },
      addressRules: [
        v => !!this.selectedPlace || 'Please select an address from the search results',
      ],
      addressLine1Rules: [
        v => !!v || 'Address Line 1 is required',
      ],
    };
  },
  mounted() {
    this.getAddress();
  },
  watch: {
    selectedPlace(newPlace) {
      if (newPlace) {
        this.formData.place_id = newPlace.place_id;
        this.formData.full_name = newPlace.full_name;
        this.formData.types = newPlace.types || [];
      } else {
        this.formData.place_id = '';
        this.formData.full_name = '';
        this.formData.types = [];
      }
    },
  },
  methods: {
    resetForm() {
      this.formData = {
        place_id: '',
        full_name: '',
        address_line_1: '',
        types: [],
        address_nickname: '',
        is_default: false,
      };
      this.selectedPlace = null;
      this.places = [];
    },
    
    handleClose() {
      this.$emit('close');
    },

    async getAddress() {
      const { data, error: apiError } = await addressService.getAddress(this.address.id);
      if (apiError.value) {
        throw new Error(apiError.value);
      }
      this.formData = data.value;
      this.selectedPlace = {
        place_id: data.value.place_id || `existing-${data.value.id}`,
        full_name: data.value.full_address,
      };
      this.places = [this.selectedPlace];
      return data.value;
    },

    handleSearch: debounce(async function(query) {
      if (!query || query.length < 2) {
        this.places = [];
        return;
      }

      this.searchLoading = true;
      try {
        const { data, error: searchError } = await placesService.searchPlaces(query);
        if (searchError.value) {
          console.error('Places search error:', searchError.value);
          this.places = [];
        } else {
          this.places = data.value || [];
        }
      } catch (error) {
        console.error('Search error:', error);
        this.places = [];
      } finally {
        this.searchLoading = false;
      }
    }, 400),

    async handleSubmit() {
      const isValid = await this.$refs.form.validate();
      if (!isValid.valid || !this.selectedPlace) return;

      this.loading = true;
      const { showSnackbar } = useSnackbar();
      
      try {
        if (this.isEditing && this.address?.id) {
          // Update existing address
          const { data, error: apiError } = await addressService.updateAddress(this.address.id, this.formData);
          
          if (apiError.value) {
            throw new Error(apiError.value);
          }
          
          showSnackbar({
            text: 'Address updated successfully!',
            state: 'success'
          });
          this.$emit('success', data.value);
        } else {
          // Create new address
          const { data, error: apiError } = await addressService.createAddress(this.formData);
          
          if (apiError.value) {
            throw new Error(apiError.value);
          }
          
          showSnackbar({
            text: 'Address added successfully!',
            state: 'success'
          });
          this.$emit('success', data.value);
        }
        
        this.handleClose();
      } catch (error) {
        console.error('Address operation failed:', error);
        showSnackbar({
          text: error.message || 'Failed to save address. Please try again.',
          state: 'error'
        });
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.address-form {
  max-width: 600px;
  width: 100%;
}
</style> 